import React, { useCallback, useMemo } from 'react';
import { Text, View, Alert } from 'react-native';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Icon as RNIcon, Input } from 'react-native-elements';
import { useDispatch } from 'react-redux';
import DropDownPicker from 'react-native-dropdown-picker';

import { BuildingSelectionProps } from '../../navigation';
import { DescriptionField, FormLabel } from '../../components/FormField';
import CircleButton from '../../components/CircleButton';
import { sendFeedbackAPI } from '../../api/adserver';
import { setLoading } from '../../store/appSlice';
import styles from './styles';
import { trackScreenView } from '../../lib/analytics';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';

type Props = {} & BuildingSelectionProps;

const dropDowmItems = [{ value: 1, label: 'General' }];

interface FeedbackFields {
  feedback_text: string;
  feedback_type: any;
}

const FeedbackScreen: React.FC<Props> = ({ navigation }: any) => {
  const dispatch = useDispatch();
  const validationSchema = Yup.object().shape({
    feedback_text: Yup.string().required(
      'Please enter your Feedback description',
    ),
    feedback_type: Yup.string().required('Please choose the Feedback type'),
  });

  const onGoBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  // dropdown state
  const [open, setOpen] = React.useState(false);

  const onSubmit = useCallback(
    async (values: FeedbackFields, actions) => {
      try {
        dispatch(setLoading({ isLoading: true }));
        actions.setSubmitting(true);
        await sendFeedbackAPI(values);
        trackScreenView('FeedbackSent');
        Alert.alert(
          'Thank you',
          'Your feedback was successfully sent',
          [
            {
              text: 'OK',
              onPress: () => navigation.goBack(),
            },
          ],
          { cancelable: false },
        );
        dispatch(setLoading({ isLoading: false }));
        actions.setSubmitting(false);
      } catch (error) {
        actions.setSubmitting(false);
        dispatch(setLoading({ isLoading: false }));
        Alert.alert(
          '',
          'Sorry, there was an error sending your feedback.\n\nPlease try again shortly.\n\nIf the problem persists, please contact your property management company.',
          [
            {
              text: 'OK',
            },
          ],
          { cancelable: false },
        );
      }
    },
    [navigation, dispatch],
  );

  const initialValues = {
    feedback_text: '',
    feedback_type: '',
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <RNIcon
          onPress={onGoBack}
          style={styles.topBack}
          name="arrow-back"
          tvParallaxProperties={null}
        />
        <Text style={styles.headerText}>Feedback</Text>
      </View>
      <View style={styles.textContainer}>
        <Text style={styles.titleText}>
          If you want to provide feedback, please complete the form below.
        </Text>
      </View>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({
          handleBlur,
          handleChange,
          touched,
          setFieldValue,
          errors,
          values,
          handleSubmit,
          isSubmitting,
          isValid,
        }) => {
          return (
            <KeyboardAwareScrollView
              keyboardShouldPersistTaps={'handled'}
              style={styles.modalContent}
            >
              <FormLabel>Feedback Type</FormLabel>
              <DropDownPicker
                zIndex={50000}
                zIndexInverse={60000}
                items={dropDowmItems}
                placeholder="Please Select"
                containerStyle={styles.dropDownContainer}
                style={styles.dropDownStyle}
                open={open}
                setOpen={setOpen}
                value={values.feedback_type}
                setValue={(item) => {
                  const feedbackTypeValue = item();
                  setFieldValue('feedback_type', feedbackTypeValue);
                }}
                listMode={'MODAL'}
                itemSeparator
              />
              {touched.feedback_type &&
                errors.feedback_type &&
                !isSubmitting && (
                  <Text style={styles.error}>{errors.feedback_type}</Text>
                )}
              <DescriptionField
                customLabelStyle={styles.description}
                inputStyle={styles.inputStyle}
                name="feedback_text"
                handleChange={handleChange}
                handleBlur={handleBlur}
                touched={touched.feedback_text}
                placeholder="Enter your feedback text"
                label="Description"
                error={errors.feedback_text}
                value={values.feedback_text}
              />
              <CircleButton
                disabled={isSubmitting || !isValid}
                iconName={'arrow-forward'}
                onPress={handleSubmit}
              />
            </KeyboardAwareScrollView>
          );
        }}
      </Formik>
    </View>
  );
};

export default FeedbackScreen;
